import get from 'lodash.get';
import omit from 'lodash.omit';

import env from '#shared/config/env.js';

import getHTML from '#backend/util/getHTML.js';
import { createLogObject } from '#backend/util/request.js';

import appError from './appError.js';
import isObject from './isObject.js';
import setGlobalItem from './setGlobalItem.js';
import * as providersEnvironment from './providersEnvironment.js';
import isEmptyObject from './isEmptyObject.js';

const { default: CookieStorage } = IS_CLIENT ? await import('#frontend/util/cookieStorage.js') : {};

/**
 * Set the same env configuration for client as for the backend so credentials and other env-specific values are correct
 * @note Expected to run on client only
 */
const updateEnvironmentConfiguration = () => {
	const cookieStorage = new CookieStorage();
	env.providers = providersEnvironment.createObject(cookieStorage.getItem('env'), env);
};

/**
 * Make error logs also with store update
 * @param {object} error Error object
 * @param {object} store Vuex store object
 * @param {string} mutation Mutation key of error setting
 */
const setError = (error, store, mutation) => {
	if (isObject(store) && typeof mutation === 'string' && store._mutations && store._mutations[mutation]) {
		store.commit(mutation, error);
	}
};

/**
 * Get all found components for current path
 * @param {object} router Vue router object
 * @param {object} newRoute New route (to)
 * @param {object} [previousRoute] (CLIENT) Previous route (from)
 * @returns {object[]} Array of components
 */
const loadComponents = (router, newRoute, previousRoute) => {
	const newComponents = router.getMatchedComponents(newRoute);
	if (!previousRoute) {
		return newComponents;
	} else {
		// Compare components from previous and new route and return list of only new ones so we wont load them again
		let diffed = false;
		const previousComponents = router.getMatchedComponents(previousRoute);
		return newComponents.filter((component, index) => {
			if (diffed) {
				return true;
			} else {
				// As "newComponents" and "previousComponents" array order is identical, stop on the first occurance
				diffed = previousComponents[index] !== component;
				return diffed;
			}
		});
	}
};

/**
 * Run components middlewares
 * @param {object[]} components Array of components
 * @param {object} middlewareData Data object passed to each middleware
 * @returns {Promise}
 */
const runMiddlewares = async (components, middlewareData) => {
	let redirect = null;
	// Add "redirect" method to middlewares to tell the app to redirect user to different URL
	middlewareData.redirect = (status, url) => {
		redirect = {
			status,
			url
		};
	};
	// Extract middlewares from all components to array
	const middlewares = components.reduce((previous, { middleware }) => {
		if (Array.isArray(middleware)) {
			previous.push(...middleware);
		}
		return previous;
	}, []);
	// Run each of the middleware in series (synchronously)
	for (const middleware of middlewares) {
		if (!redirect) {
			await middleware(middlewareData);
		}
	}
	return redirect;
};

/**
 * Find parent component by name when checking all extends of current component
 * @param {string} name Name of a parent component we are looking for
 * @param {object} component Vue component
 * @returns {boolean}
 */
const findParentComponent = (name, component) => {
	if (!isEmptyObject(component.extends)) {
		return findParentComponent(name, component.extends);
	}
	return component.name === name;
};

/**
 * Do actions when route is loaded
 * @param {object} data
 * @param {object} data.app Vue instance
 * @param {object} data.options Theme options
 * @param {object} data.router VueRouter instance
 * @param {object} data.store Vuex instance
 * @param {object} serverContext (SERVER ONLY)
 * @param {object} [serverContext.request] Express request object
 * @param {object} [serverContext.response] Express response object
 * @param {object} to
 * @param {object} [from] (CLIENT ONLY)
 * @returns {Promise<object>} Object with responseHeaders, responseStatus and responseString
 */
const loadComponent = async (data, serverContext = {}, to, from) => {
	const components = loadComponents(data.router, to, from);
	const [errorComponent] = components.filter((component) => findParentComponent('CError', component));
	// Check if error component found and if it's the only one
	if (IS_SERVER && components.length === 1 && errorComponent) {
		const error = appError('Page not found', {
			code: 'APP_SHARED_UTIL',
			origin: 'entry.loadComponent',
			status: 404,
			description: 'No defined routes matched the requested URL',
			detail: createLogObject(serverContext.request)
		}, 'warn');
		setError(error, data.store, 'SET_ERROR');
	}
	// Run middlewares for parent and all found components with custom data
	try {
		const redirect = await runMiddlewares([data.options.parentComponent, ...components], {
			app: data.app,
			router: data.router,
			route: IS_SERVER ? data.router.currentRoute : to,
			store: data.store,
			request: serverContext.request,
			response: serverContext.response,
			isClientProcess: IS_CLIENT,
			isServerProcess: IS_SERVER,
			env
		});
		// If redirect is an object with redirect information, proceed
		if (isObject(redirect) && typeof redirect.url === 'string' && typeof redirect.status === 'number') {
			return {
				responseStatus: redirect.status,
				responseRedirect: redirect.url
			};
		}
	} catch (error) {
		// If any error found in the middlewares, make log and set the store error
		appError(error, {
			code: 'APP_SHARED_UTIL',
			origin: 'entry.loadComponent',
			status: 500,
			description: 'Unhandled exception occured while processing shared middlewares'
		});
		setError(error, data.store, 'SET_ERROR');
	}
	const responseHeaders = {};
	let responseStatus = 200;
	let responseString = '';
	if (IS_SERVER) {
		const locals = serverContext.response.locals;
		const state = data.store.state;
		const getters = data.store.getters;
		const initialState = setGlobalItem('initialState', {
			...state,
			error: isObject(state.error) ? omit(state.error, ['stack', 'app.detail.request']) : state.error, // Hide sensitive information from the error object
			route: null // We don't want to expose the server route object and so the client one will be used instead
		});
		responseHeaders['x-checkout-origin'] = state.app.requestHash;
		responseStatus = get(state, 'error.app.status', responseStatus);
		responseString = await getHTML(locals.theme.paths.templateHTMLPath, data.app, Object.assign({}, state, {
			t: getters.getLocalization,
			initialState
		}));
	}
	return {
		responseHeaders,
		responseStatus,
		responseString
	};
};

/**
 * Entry object for both server and client
 * @param {object} data
 * @param {object} data.app Vue instance
 * @param {object} data.router VueRouter instance
 * @param {object} data.store Vuex instance
 * @param {object} data.options Theme options
 */
const loadClientEntry = (data) => {
	if (isObject(window.avastGlobals) && isObject(window.avastGlobals.cart.initialState)) {
		// Set default state data for clinet store object
		data.store.replaceState({
			...window.avastGlobals.cart.initialState,
			route: data.store.state.route // Use "route" property from client store to prevent "NavigationDuplicated" error
		});
	}
	// Load correct environment based on server response
	updateEnvironmentConfiguration();
	// When router is ready, handle loaded components
	data.router.onReady(() => {
		loadComponent(data, {}, data.store.state.route);
		data.router.beforeEach(async (to, from, next) => {
			const entryResult = await loadComponent(data, {}, to, from);
			if (entryResult.responseRedirect) {
				next(entryResult.responseRedirect);
			} else {
				next();
			}
		});
		data.app.$mount('#root');
	});
};

/**
 * Entry object for server
 * @param {object} data
 * @param {object} data.app Vue instance
 * @param {object} data.router VueRouter instance
 * @param {object} data.store Vuex instance
 * @param {object} data.options Theme options
 * @param {object} serverContext
 * @param {object} serverContext.request Express request object
 * @param {object} serverContext.response Express response object
 * @returns {Promise<object>}
 */
const loadServerEntry = (data, serverContext) => {
	return new Promise((resolve) => {
		data.router.push(serverContext.request.originalUrl);
		data.router.onReady(async () => {
			const entryResult = await loadComponent(data, serverContext, data.store.state.route);
			resolve(entryResult);
		});
	});
};

export default IS_SERVER ? (data) => (serverContext) => loadServerEntry(data, serverContext) : loadClientEntry;
